import React, { useState } from "react";
import { Loader2, Lock, Eye, EyeOff, X } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

/**
 * Modal that lets the signed-in user change their password.
 *
 *  - Calls POST /api/auth/change-password (server validates current password,
 *    enforces >=8 chars, refuses no-op changes).
 *  - Keeps the password on the client only long enough to send it; clears state
 *    on close.
 *  - Returns focus to the trigger when closed.
 */
export default function ChangePasswordModal({ open, onClose }) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const close = () => {
    if (busy) return;
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setShowCurrent(false);
    setShowNew(false);
    setErr("");
    onClose?.();
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!currentPassword) {
      setErr("Enter your current password.");
      return;
    }
    if (newPassword.length < 8) {
      setErr("New password must be at least 8 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setErr("New password and confirmation don't match.");
      return;
    }
    if (newPassword === currentPassword) {
      setErr("New password must be different from the current one.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success("Password updated");
      close();
    } catch (e2) {
      const msg = formatApiError(e2.response?.data?.detail) || "Could not change password";
      setErr(msg);
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-slate-900/60 backdrop-blur-sm p-4"
      onClick={close}
      data-testid="change-password-overlay"
    >
      <div
        className="bg-white rounded-sm border-2 border-slate-900 shadow-2xl w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
        data-testid="change-password-modal"
      >
        <header className="px-5 py-4 border-b-2 border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Lock strokeWidth={2.5} className="h-5 w-5 text-amber-600" />
            <h2 className="font-display text-lg uppercase tracking-tight">Change password</h2>
          </div>
          <button
            type="button"
            onClick={close}
            disabled={busy}
            data-testid="change-password-close"
            className="rg-tap p-1.5 rounded-sm hover:bg-slate-100"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <form onSubmit={onSubmit} className="p-5 space-y-3">
          <PasswordField
            label="Current password"
            value={currentPassword}
            onChange={setCurrentPassword}
            show={showCurrent}
            onToggle={() => setShowCurrent((v) => !v)}
            testid="cp-current"
            autoFocus
          />
          <PasswordField
            label="New password"
            value={newPassword}
            onChange={setNewPassword}
            show={showNew}
            onToggle={() => setShowNew((v) => !v)}
            testid="cp-new"
            hint="At least 8 characters"
          />
          <PasswordField
            label="Confirm new password"
            value={confirmPassword}
            onChange={setConfirmPassword}
            show={showNew}
            onToggle={() => setShowNew((v) => !v)}
            testid="cp-confirm"
          />

          {err && (
            <div
              className="text-sm font-bold text-rose-700 bg-rose-50 border-2 border-rose-200 px-3 py-2 rounded-sm"
              data-testid="cp-error"
            >
              {err}
            </div>
          )}

          <div className="pt-2 flex items-center gap-2 justify-end">
            <button
              type="button"
              onClick={close}
              disabled={busy}
              data-testid="cp-cancel"
              className="rg-tap px-4 border-2 border-slate-300 hover:border-slate-900 rounded-sm bg-white font-bold uppercase tracking-wide text-sm text-slate-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy}
              data-testid="cp-submit"
              className="rg-tap px-4 border-2 border-amber-700 hover:border-amber-900 rounded-sm bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold uppercase tracking-wide text-sm flex items-center gap-2 disabled:opacity-60"
            >
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              {busy ? "Saving…" : "Update password"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function PasswordField({ label, value, onChange, show, onToggle, testid, hint, autoFocus }) {
  return (
    <label className="block">
      <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-600">{label}</span>
      <div className="mt-1 relative">
        <input
          type={show ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoFocus={!!autoFocus}
          autoComplete={testid === "cp-current" ? "current-password" : "new-password"}
          data-testid={testid}
          className="rg-tap w-full min-h-[42px] px-3 pr-10 border-2 border-slate-300 focus:border-slate-900 rounded-sm bg-white text-sm font-mono"
        />
        <button
          type="button"
          onClick={onToggle}
          data-testid={`${testid}-toggle`}
          className="absolute inset-y-0 right-0 px-2 grid place-items-center text-slate-500 hover:text-slate-900"
          aria-label={show ? "Hide password" : "Show password"}
        >
          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
      {hint && <p className="text-[11px] text-slate-500 mt-1">{hint}</p>}
    </label>
  );
}
